import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { User, Lock, Save, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { api } from "../../../lib/api";
import { useAuthStore } from "../../../stores/authStore";
import { useAdminT } from "../../../hooks/useAdminT";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { FormField } from "../components/shared/FormField";

// ── Types
interface ProfileForm { name: string; email: string; }
interface PasswordForm { currentPassword: string; newPassword: string; confirmPassword: string; }

// ── Small reusable password input
function PasswordInput({ id, placeholder, register, showToggle }: any) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input
                id={id}
                type={show ? "text" : "password"}
                {...register}
                placeholder={placeholder}
                className="w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            {showToggle && (
                <button
                    type="button"
                    onClick={() => setShow(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                >
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
            )}
        </div>
    );
}

export default function ProfilePage() {
    const { isRtl } = useAdminT();
    const { user, token } = useAuthStore();
    const { handleServerErrors } = useFormValidation();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<"info" | "password">("info");

    // ── Profile form
    const profileForm = useForm<ProfileForm>({
        defaultValues: { name: user?.name ?? "", email: user?.email ?? "" },
    });

    const profileMutation = useMutation({
        mutationFn: (data: ProfileForm) =>
            api.put("/api/admin/me", data, { headers: { Authorization: `Bearer ${token}` } }),
        onSuccess: (res) => {
            // Patch zustand store so the sidebar name updates immediately
            const updated = res.data?.data;
            if (updated) {
                useAuthStore.setState(s => ({
                    user: s.user ? { ...s.user, name: updated.name, email: updated.email } : s.user
                }));
            }
            toast.success("Profile updated successfully");
            queryClient.invalidateQueries({ queryKey: ["breadcrumb-project"] });
        },
        onError: (err: any) => handleServerErrors(err, profileForm.setError, "Failed to update profile"),
    });

    // ── Password form
    const passwordForm = useForm<PasswordForm>({
        defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
    });

    const passwordMutation = useMutation({
        mutationFn: (data: PasswordForm) =>
            api.put("/api/admin/me/password", {
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
            }, { headers: { Authorization: `Bearer ${token}` } }),
        onSuccess: () => {
            toast.success("Password changed successfully");
            passwordForm.reset();
        },
        onError: (err: any) => {
            const errData = err?.response?.data;
            // Map backend field errors
            if (errData?.fields) {
                Object.entries(errData.fields).forEach(([field, msg]) =>
                    passwordForm.setError(field as any, { type: "server", message: msg as string })
                );
                toast.error("Please fix the highlighted fields");
            } else {
                toast.error(errData?.message || "Failed to change password");
            }
        },
    });

    const onSubmitPassword = (data: PasswordForm) => {
        if (data.newPassword !== data.confirmPassword) {
            passwordForm.setError("confirmPassword", { type: "manual", message: "Passwords do not match" });
            return;
        }
        passwordMutation.mutate(data);
    };

    const tabs = [
        { id: "info" as const, label: "Profile Info", icon: User },
        { id: "password" as const, label: "Change Password", icon: Lock },
    ];

    return (
        <div className="max-w-2xl mx-auto px-4 py-8" dir={isRtl ? "rtl" : "ltr"}>
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {user?.name?.charAt(0)?.toUpperCase() ?? "A"}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-secondary">{user?.name}</h1>
                        <p className="text-sm text-slate-500">{user?.role}</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id
                            ? "border-primary text-primary"
                            : "border-transparent text-slate-500 hover:text-slate-700"
                            }`}
                    >
                        <tab.icon size={15} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Profile Info Tab */}
            {activeTab === "info" && (
                <form
                    onSubmit={profileForm.handleSubmit(d => profileMutation.mutate(d))}
                    className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4"
                >
                    <FormField
                        label="Full Name"
                        error={profileForm.formState.errors.name?.message}
                        required
                    >
                        <input
                            {...profileForm.register("name", { required: "Name is required", minLength: { value: 2, message: "At least 2 characters" } })}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                            placeholder="Your full name"
                        />
                    </FormField>

                    <FormField
                        label="Email Address"
                        error={profileForm.formState.errors.email?.message}
                        required
                    >
                        <input
                            {...profileForm.register("email", {
                                required: "Email is required",
                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email format" }
                            })}
                            type="email"
                            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                            placeholder="your@email.com"
                        />
                    </FormField>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={profileMutation.isPending}
                            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-60 transition-colors"
                        >
                            <Save size={15} />
                            {profileMutation.isPending ? "Saving…" : "Save Changes"}
                        </button>
                    </div>
                </form>
            )}

            {/* Change Password Tab */}
            {activeTab === "password" && (
                <form
                    onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
                    className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4"
                >
                    <FormField
                        label="Current Password"
                        error={passwordForm.formState.errors.currentPassword?.message}
                        required
                    >
                        <PasswordInput
                            id="currentPassword"
                            placeholder="Your current password"
                            register={passwordForm.register("currentPassword", { required: "Current password is required" })}
                            showToggle
                        />
                    </FormField>

                    <FormField
                        label="New Password"
                        error={passwordForm.formState.errors.newPassword?.message}
                        hint="Minimum 6 characters"
                        required
                    >
                        <PasswordInput
                            id="newPassword"
                            placeholder="New password"
                            register={passwordForm.register("newPassword", {
                                required: "New password is required",
                                minLength: { value: 6, message: "At least 6 characters" }
                            })}
                            showToggle
                        />
                    </FormField>

                    <FormField
                        label="Confirm New Password"
                        error={passwordForm.formState.errors.confirmPassword?.message}
                        required
                    >
                        <PasswordInput
                            id="confirmPassword"
                            placeholder="Confirm new password"
                            register={passwordForm.register("confirmPassword", { required: "Please confirm your password" })}
                            showToggle
                        />
                    </FormField>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={passwordMutation.isPending}
                            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-60 transition-colors"
                        >
                            <Lock size={15} />
                            {passwordMutation.isPending ? "Changing…" : "Change Password"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
